<template>
  <footer class="relative overflow-hidden w-full font-sans transition-colors duration-300
                 text-slate-600 dark:text-ink-400">
    
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 z-0 transition-colors duration-300
                                   bg-slate-50/50 dark:bg-ink-950">
      <div
        class="absolute -top-40 -left-40 h-96 w-96 rounded-full blur-3xl mix-blend-multiply animate-blob
               bg-brand-100/50 dark:bg-brand-900/10 dark:mix-blend-normal">
      </div>
      <div
        class="absolute -bottom-40 -right-40 h-96 w-96 rounded-full blur-3xl mix-blend-multiply animate-blob animation-delay-2000
               bg-brand-100/50 dark:bg-brand-900/10 dark:mix-blend-normal">
      </div>
    </div>

    <div class="relative z-10">
      <transition name="fade-slide">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" v-if="isEditMode">
          <div class="flex justify-end">
            <button @click="emitEdit"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer active:scale-95
                     text-slate-700 bg-white border border-slate-200 hover:bg-slate-800 hover:text-white hover:border-transparent
                     dark:text-ink-200 dark:bg-ink-800 dark:border-ink-700 dark:hover:bg-white dark:hover:text-ink-900">
              <i class="fas fa-pen-to-square"></i> Éditer le footer
            </button>
          </div>
        </div>
      </transition>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">

          <div
            :class="['lg:col-span-5 flex flex-col h-full backdrop-blur-md rounded-3xl p-8 ring-1 shadow-sm transition-all duration-700 ease-out hover:shadow-lg hover:-translate-y-1',
                     'bg-white/70 ring-slate-200 hover:ring-brand-200/60',
                     'dark:bg-ink-900/60 dark:ring-ink-800 dark:hover:ring-brand-900/50',
                     isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']">
            <div class="flex-1">
              <h3 class="text-2xl font-black tracking-tight break-words
                         text-slate-800 dark:text-ink-100">
                {{ main?.titre || 'Anjah R.' }}
              </h3>
              <p class="mt-4 text-base leading-relaxed text-pretty
                        text-slate-600 dark:text-ink-400">
                {{ main?.description || 'Votre partenaire expert en transformation numérique.' }}
              </p>
            </div>

            <div class="mt-8 pt-6 border-t border-slate-100 dark:border-ink-800">
              <p class="text-xs font-bold uppercase mb-4
                        text-slate-400 dark:text-ink-500">Suivez-nous</p>
              <div class="flex flex-wrap gap-3">
                <a v-for="s in socialsNormalized" :key="s.id" :href="s.href" target="_blank"
                  class="group flex items-center justify-center h-10 w-10 rounded-full transition-all duration-300 shadow-sm cursor-pointer hover:scale-110 active:scale-90
                         bg-white border border-slate-200 text-slate-500 hover:bg-slate-900 hover:text-white
                         dark:bg-ink-800 dark:border-ink-700 dark:text-ink-400 dark:hover:bg-white dark:hover:text-ink-950">
                  <i :class="s.iconClass" class="text-lg transition-transform group-hover:rotate-6"></i>
                </a>
              </div>
            </div>
          </div>

          <nav
            :class="['lg:col-span-3 flex flex-col h-full backdrop-blur-md rounded-3xl p-8 ring-1 shadow-sm transition-all duration-700 ease-out delay-150 hover:shadow-lg hover:-translate-y-1',
                     'bg-white/70 ring-slate-200 hover:ring-brand-200/60',
                     'dark:bg-ink-900/60 dark:ring-ink-800 dark:hover:ring-brand-900/50',
                     isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']">
            <h4 class="text-sm font-bold tracking-widest uppercase mb-6 border-b pb-2
                       text-slate-800 border-slate-100
                       dark:text-ink-200 dark:border-ink-800">
              Explorer
            </h4>

            <ul class="space-y-3 flex-1">
              <li v-for="l in linksNormalized" :key="l.id">
                <component :is="l.internal ? 'router-link' : 'a'" :to="l.internal ? l.href : undefined"
                  :href="!l.internal ? l.href : undefined" :target="!l.internal ? '_blank' : undefined"
                  class="group flex items-center gap-3 p-2 -mx-2 rounded-lg transition-all duration-200 cursor-pointer w-full hover:pl-3
                         hover:bg-white dark:hover:bg-ink-800/50">
                  
                  <span
                    class="flex shrink-0 items-center justify-center h-6 w-6 rounded transition-colors duration-300
                           bg-brand-50 text-brand-600 group-hover:bg-brand-600 group-hover:text-white
                           dark:bg-brand-900/30 dark:text-brand-400 dark:group-hover:bg-brand-500 dark:group-hover:text-white">
                    <i class="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                  </span>
                  
                  <span class="text-sm font-medium break-words transition-colors
                               group-hover:text-slate-900 dark:text-ink-400 dark:group-hover:text-ink-100">
                    {{ l.label || 'Lien' }}
                  </span>
                </component>
              </li>
            </ul>
          </nav>

          <div
            :class="['lg:col-span-4 flex flex-col h-full backdrop-blur-md rounded-3xl p-8 ring-1 shadow-sm transition-all duration-700 ease-out delay-300 hover:shadow-lg hover:-translate-y-1',
                     'bg-white/70 ring-slate-200 hover:ring-brand-200/60',
                     'dark:bg-ink-900/60 dark:ring-ink-800 dark:hover:ring-brand-900/50',
                     isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12']">
            <h4 class="text-sm font-bold tracking-widest uppercase mb-6 border-b pb-2
                       text-slate-800 border-slate-100
                       dark:text-ink-200 dark:border-ink-800">
              Contact
            </h4>

            <div class="flex flex-col gap-3 flex-1">
              <a v-for="c in contactWithLinks" :key="c.id" :href="c.link"
                :target="c.type === 'address' ? '_blank' : undefined"
                class="group flex items-start gap-4 p-3 rounded-2xl border transition-all duration-300 cursor-pointer w-full hover:scale-[1.02]
                       bg-white/50 border-slate-100 hover:bg-white hover:border-brand-200
                       dark:bg-ink-800/40 dark:border-ink-700/50 dark:hover:bg-ink-800 dark:hover:border-ink-600">
                
                <div
                  class="shrink-0 h-10 w-10 rounded-lg flex items-center justify-center shadow-sm ring-1 transition-all duration-300
                         bg-white text-brand-600 ring-slate-100 group-hover:bg-brand-600 group-hover:text-white group-hover:scale-105
                         dark:bg-ink-700 dark:text-brand-400 dark:ring-ink-600 dark:group-hover:bg-brand-600 dark:group-hover:text-white">
                  <i :class="c.iconClass" class="text-base"></i>
                </div>

                <div class="flex-1 min-w-0 pt-0.5">
                  <p
                    class="text-[10px] font-bold uppercase leading-none mb-1 transition-colors
                           text-slate-400 group-hover:text-brand-400
                           dark:text-ink-500 dark:group-hover:text-brand-300">
                    {{ c.labelDisplay }}
                  </p>
                  <p
                    :class="['text-sm font-semibold transition-colors leading-tight', 
                             c.type === 'email' ? 'break-all' : 'break-words',
                             'text-slate-800 group-hover:text-brand-700',
                             'dark:text-ink-200 dark:group-hover:text-white']">
                    {{ c.value || 'Information' }}
                  </p>
                </div>
              </a>
            </div>
          </div>

        </div>

        <div
          :class="['mt-12 pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4 transition-opacity duration-1000 delay-700',
                   'border-slate-200 dark:border-ink-800',
                   isVisible ? 'opacity-100' : 'opacity-0']">
          <p class="text-xs sm:text-sm font-medium text-center md:text-left
                    text-slate-500 dark:text-ink-500">
            {{ note?.description || '© 2025 Anjah R.. Tous droits réservés.' }}
          </p>
          <div class="order-1 md:order-2 flex items-center gap-2 opacity-60 grayscale hover:grayscale-0 transition-all dark:opacity-40 dark:hover:opacity-100">
            <i class="fa-solid fa-shield-halved text-xs"></i>
            <span class="text-xs">Secure Site</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
// Le script n'a pas besoin de changement
import http from "@/api/http";

export default {
  name: "FooterComponent",
  props: { isEditMode: { type: Boolean, default: false } },
  data() {
    return {
      main: null,
      links: [],
      socials: [],
      contact: [],
      note: null,
      loading: false,
      isVisible: false,
    };
  },
  computed: {
    currentLang() {
      const fromStore = this.$store?.state?.langue;
      const fromRoute = this.$route?.params?.langue;
      const lang = fromStore || fromRoute || "fr";
      return ["fr", "en"].includes(lang) ? lang : "fr";
    },
    linksNormalized() {
      return (this.links || []).map((l) => {
        const raw = (l.description || "").trim();
        const isExternal = /^https?:\/\//i.test(raw);
        const href = isExternal ? raw : this.localizePath(raw);
        return { id: l.id, label: l.titre || "Lien", href, internal: !isExternal };
      });
    },
    socialsNormalized() {
      return (this.socials || []).map((s) => {
        const key = (s.titre || "").toLowerCase().trim();
        return { id: s.id, href: s.description || "#", iconClass: this.mapSocialIcon(key) };
      });
    },
    contactWithLinks() {
      return (this.contact || []).map((c) => {
        const type = (c.titre || "").toLowerCase().trim();
        const value = c.description || "";
        let link = "#";
        let labelDisplay = "Info";
        if (type === 'email') { link = `mailto:${value}`; labelDisplay = "Email"; } 
        else if (type === 'phone') { const cleanPhone = value.replace(/[^\d+]/g, ''); link = `tel:${cleanPhone}`; labelDisplay = "Téléphone"; } 
        else if (type === 'address') { link = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`; labelDisplay = "Adresse"; }
        return { id: c.id, type, value, link, labelDisplay, iconClass: this.mapContactIcon(type) };
      });
    }
  },
  watch: { currentLang() { this.fetchFooter(); }, },
  mounted() { this.fetchFooter(); setTimeout(() => { this.isVisible = true; }, 100); },
  methods: {
    async fetchFooter() {
      try {
        this.loading = true;
        const { data } = await http.get("footer-content", { params: { langue: this.currentLang } });
        this.main = data.main; this.links = data.links; this.socials = data.socials; this.contact = data.contact; this.note = data.note;
      } catch (e) { console.error("Erreur footer:", e); } finally { this.loading = false; }
    },
    localizePath(raw) {
      let p = raw.startsWith("/") ? raw : `/${raw}`;
      if (/^\/(fr|en)\//i.test(p)) return p.replace(/^\/(fr|en)\//i, `/${this.currentLang}/`);
      return `/${this.currentLang}${p === '/' ? '' : p}`;
    },
    mapSocialIcon(key) {
      const map = { linkedin: 'fa-brands fa-linkedin-in', facebook: 'fa-brands fa-facebook-f', instagram: 'fa-brands fa-instagram', twitter: 'fa-brands fa-x-twitter', x: 'fa-brands fa-x-twitter', youtube: 'fa-brands fa-youtube', tiktok: 'fa-brands fa-tiktok', github: 'fa-brands fa-github' };
      return map[key] || 'fa-solid fa-globe';
    },
    mapContactIcon(type) {
      const map = { address: 'fa-solid fa-location-dot', email: 'fa-solid fa-envelope', phone: 'fa-solid fa-phone' };
      return map[type] || 'fa-solid fa-info';
    },
    emitEdit() { this.$emit("edit", { main: this.main, links: this.links, socials: this.socials, contact: this.contact, note: this.note, langue: this.currentLang, }); },
  },
};
</script>

<style scoped>
@keyframes blob { 0% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } 100% { transform: translate(0px, 0px) scale(1); } }
.animate-blob { animation: blob 7s infinite; }
.animation-delay-2000 { animation-delay: 2s; }
.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.4s ease; }
.fade-slide-enter-from, .fade-slide-leave-to { opacity: 0; transform: translateY(-20px); }
footer { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
</style>